import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import './PaymentModal.css';
import axios from 'axios';

const API_URL = 'https://craftmystore.vercel.app';

const PaymentModal = ({ isOpen, onClose, defaultEcommPlan = 'pro', defaultHostingPlan = 'hosting6Month' }) => {
    const { getPrice, getCurrencySymbol, selectedCurrency } = useCurrency();
    const [ecommPlan, setEcommPlan] = useState(defaultEcommPlan);
    const [hostingPlan, setHostingPlan] = useState(defaultHostingPlan);
    const [total, setTotal] = useState(0);
    const [selectedPayment, setSelectedPayment] = useState('cashfree');
    const [loading, setLoading] = useState(false);
    const [customerName, setCustomerName] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const navigate = useNavigate();

    // Update selections when default props change
    useEffect(() => {
        setEcommPlan(defaultEcommPlan);
        setHostingPlan(defaultHostingPlan);
    }, [defaultEcommPlan, defaultHostingPlan]);

    useEffect(() => {
        calculateTotal();
    }, [ecommPlan, hostingPlan, selectedCurrency]);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    const calculateTotal = () => {
        const ecommPrice = getPrice(ecommPlan);
        let hostingPrice = getPrice(hostingPlan);

        // Multiply hosting price by duration
        if (hostingPlan === 'hosting3Month') {
            hostingPrice *= 3;
        } else if (hostingPlan === 'hosting6Month') {
            hostingPrice *= 6;
        } else if (hostingPlan === 'hosting12Month') {
            hostingPrice *= 12;
        }

        const newTotal = Number(ecommPrice) + Number(hostingPrice);
        setTotal(newTotal);
    };

    const handlePayment = async () => {
        if (!customerName || !customerEmail || !customerPhone) {
            alert('Please fill in all required fields');
            return;
        }

        setLoading(true);

        try {
            const paymentDetails = {
                amount: total,
                currency: selectedCurrency,
                customerName,
                customerEmail,
                customerPhone,
                ecommPlan,
                hostingPlan
            };

            // PhonePe implementation commented out
            /*
            if (selectedPayment === 'phonepe') {
                console.log('Initiating PhonePe payment...');
                response = await axios.post(`${API_URL}/api/payment/initiate-phonepe`, paymentDetails);
                if (response.data && response.data.success) {
                    localStorage.setItem('paymentRef', response.data.merchantTransactionId);
                    localStorage.setItem('paymentMethod', 'phonepe');
                    localStorage.setItem('paymentAmount', total);
                    localStorage.setItem('customerDetails', JSON.stringify({
                        name: customerName,
                        email: customerEmail,
                        phone: customerPhone
                    }));
                    window.location.href = response.data.redirectUrl;
                } else {
                    alert(`Payment initiation failed: ${response.data?.message || 'Unknown error'}`);
                }
            } else
            */

            if (selectedPayment === 'cashfree') {
                console.log('Initiating Cashfree payment...');
                const response = await axios.post(`${API_URL}/api/payment/initiate-cashfree`, paymentDetails);

                if (response.data && response.data.success) {
                    localStorage.setItem('paymentRef', response.data.merchantTransactionId);
                    localStorage.setItem('paymentMethod', 'cashfree');
                    localStorage.setItem('paymentAmount', total);
                    localStorage.setItem('customerDetails', JSON.stringify({
                        name: customerName,
                        email: customerEmail,
                        phone: customerPhone
                    }));

                    if (!window.Cashfree) {
                        console.error("Cashfree SDK not loaded");
                        alert("Payment gateway not available. Please try again later.");
                        setLoading(false);
                        return;
                    }

                    const sessionId = response.data.payment_session_id;
                    if (!sessionId) {
                        console.error("No payment session ID found");
                        alert("Payment initialization failed. Please try again.");
                        setLoading(false);
                        return;
                    }

                    const cashfree = window.Cashfree({
                        mode: 'production'
                    });

                    try {
                        cashfree.checkout({
                            paymentSessionId: sessionId,
                            redirectTarget: '_self'
                        });
                    } catch (sdkError) {
                        console.error("Cashfree SDK error:", sdkError);
                        alert("Payment gateway error. Please try again later.");
                        setLoading(false);
                    }
                } else {
                    alert(`Payment initiation failed: ${response.data?.message || 'Unknown error'}`);
                }
            }
        } catch (error) {
            console.error('Payment error:', error);
            let errorMessage = 'Payment processing error. Please try again later.';

            if (error.response) {
                errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'No response from server. Please check your connection.';
            }

            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div id="paymentOverlay" className="overlay">
            <div className="payment-modal-container">
                <div className="payment-modal-content">
                    {/* Close Button */}
                    <div className="close-button" onClick={onClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <line x1={18} y1={6} x2={6} y2={18} />
                            <line x1={6} y1={6} x2={18} y2={18} />
                        </svg>
                    </div>
                    <p className="title">Choose your Plan</p>

                    {/* Ecommerce Plan Section */}
                    <p className="subtitle">Choosing your Ecommerce plan</p>
                    <select
                        id="ecommPlan"
                        className="plan-select"
                        value={ecommPlan}
                        onChange={(e) => setEcommPlan(e.target.value)}
                    >
                        <option value="starter">Starter Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('starter')}</option>
                        <option value="pro">Pro Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('pro')}</option>
                        <option value="business">Business Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('business')}</option>
                        <option value="enterprise">Enterprise Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('enterprise')}</option>
                    </select>

                    {/* Hosting Plan Section */}
                    <p className="subtitle">Choose your Hosting Plan</p>
                    <select
                        id="hostingPlan"
                        className="plan-select"
                        value={hostingPlan}
                        onChange={(e) => setHostingPlan(e.target.value)}
                    >
                        <option value="hosting1Month">1 Month Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('hosting1Month')}</option>
                        <option value="hosting3Month">3 Month Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('hosting3Month') * 3}</option>
                        <option value="hosting6Month">6 Month Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('hosting6Month') * 6}</option>
                        <option value="hosting12Month">12 Month Plan: {getCurrencySymbol(selectedCurrency)}{getPrice('hosting12Month') * 12}</option>
                    </select>

                    {/* Customer Details */}
                    <p className="subtitle">Your Details</p>
                    <input
                        type="text"
                        placeholder="Full Name"
                        className="customer-input"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        required
                    />
                    <input
                        type="email"
                        placeholder="Email Address"
                        className="customer-input"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        required
                    />
                    <input
                        type="tel"
                        placeholder="Phone Number"
                        className="customer-input"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        required
                    />

                    {/* Total Section */}
                    <div className="total-section">
                        <p className="total-label">Total:</p>
                        <p id="totalAmount" className="total-amount">{getCurrencySymbol(selectedCurrency)}{total}</p>
                    </div>

                    {/* Payment Methods Section */}
                    {/* <p className="subtitle">Choose a Payment Provider</p> */}

                    {/* Payment Methods Container */}
                    {/*
                    <div className="payment-methods-container">
                        {/* PhonePe (commented out) */}
                    {/*
                        <label className="payment-method-card">
                            <div className="payment-method-content">
                                <div className="payment-method-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} viewBox="0 0 24 24">
                                        <path fill="#1e293b" d="M10.206 9.941h2.949v4.692c-.402.201-.938.268-1.34.268c-1.072 0-1.609-.536-1.609-1.743V9.941zm13.47 4.816c-1.523 6.449-7.985 10.442-14.433 8.919C2.794 22.154-1.199 15.691.324 9.243C1.847 2.794 8.309-1.199 14.757.324c6.449 1.523 10.442 7.985 8.919 14.433zm-6.231-5.888a.887.887 0 0 0-.871-.871h-1.609l-3.686-4.222c-.335-.402-.871-.536-1.407-.402l-1.274.401c-.201.067-.268.335-.134.469l4.021 3.82H6.386c-.201 0-.335.134-.335.335v.67c0 .469.402.871.871.871h.938v3.217c0 2.413 1.273 3.82 3.418 3.82c.67 0 1.206-.067 1.877-.335v2.145c0 .603.469 1.072 1.072 1.072h.938a.432.432 0 0 0 .402-.402V9.874h1.542c.201 0 .335-.134.335-.335v-.67z" />
                                    </svg>
                                </div>
                                <div className="payment-method-details">
                                    <p className="payment-method-title">PhonePe</p>
                                    <p className="payment-method-subtitle">Only for India</p>
                                    <p className="payment-method-description">For Net Banking, Card and UPI</p>
                                </div>
                            </div>
                            <input
                                type="radio"
                                name="payment"
                                value="phonepe"
                                checked={selectedPayment === 'phonepe'}
                                onChange={() => setSelectedPayment('phonepe')}
                            />
                        </label>
                        <label className="payment-method-card">
                            <div className="payment-method-content">
                                <div className="payment-method-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} viewBox="0 0 65.2 65.1">
                                        <g>
                                            <path
                                                fill="#1e293b"
                                                d="M25.3,0.1c-5.9,0-10.8,4.8-10.8,10.7l0,0h39.9C60.3,10.8,65.2,6,65.2,0l0,0L25.3,0.1L25.3,0.1z"
                                            />
                                            <path
                                                fill="#1e293b"
                                                d="M14.5,10.8C14.5,4.9,19.3,0,25.3,0l0,0v39.9c0,5.9-4.8,10.8-10.8,10.8l0,0V10.8z"
                                            />
                                            <path
                                                fill="#1e293b"
                                                d="M28.7,14.4v10.8h12.5c5.9,0,10.8-4.8,10.8-10.8l0,0H28.7z"
                                            />
                                            <path
                                                fill="#1e293b"
                                                d="M0,25.2c0-5.9,4.8-10.8,10.8-10.8l0,0v39.9C10.8,60.2,6,65.1,0,65.1l0,0C0,65.1,0,25.2,0,25.2z"
                                            />
                                        </g>
                                    </svg>

                                </div>
                                <div className="payment-method-details">
                                    <p className="payment-method-title">Cashfree</p>
                                    <p className="payment-method-description">For cards, net banking, UPI & more</p>
                                </div>
                            </div>
                            <input
                                type="radio"
                                name="payment"
                                value="cashfree"
                                checked={selectedPayment === 'cashfree'}
                                onChange={() => setSelectedPayment('cashfree')}
                            />
                        </label>
                    </div>
                    */}

                    {/* Proceed to Pay Button */}
                    <button
                        className="proceed-btn"
                        onClick={handlePayment}
                        disabled={loading}
                    >
                        {loading
                            ? 'Processing...'
                            : `Proceed to Pay`}
                    </button>

                    <a
                        onClick={() => navigate('/contact')}
                        className="support-link"
                    >
                        Facing Issue in the Payment? Contact Us
                    </a>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;